import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

/**
 * Last-viewed recall module (phase 05 red-team — the ONE sanctioned client-DOM
 * exception, scoped to the drawer's terminal Empty state).
 *
 * Liquid cannot read localStorage, and the empty-cart swap
 * (`cart-items-component#onLineItemRemove` -> `replaceChildren` of
 * `#empty-cart-template`) destroys any client-injected DOM — so this module
 * does the opposite of a state machine: it reads Horizon's native
 * `RecentlyViewed.getProducts()` (device-lifetime localStorage, 4-item cap,
 * NOT "session data"), fetches ONE rendered recall cell via the Section
 * Rendering API, and swaps it into the empty-state slot the server already
 * rendered with a curated fallback. It never decides cart contents, sequencing,
 * or suggestion state — those stay 100% server Liquid (vellatini-cart-suggest).
 *
 * L4: the fetch is deferred to the cart drawer's first `theme-drawer:open`
 * rather than firing on page load — the recall row is invisible until the
 * drawer opens, so fetching earlier only spends request budget without ever
 * showing the result sooner.
 *
 * M2: the `lines-update` promise resolves BEFORE morph.js applies the
 * server's section HTML to the DOM. Filling the slot synchronously on that
 * promise used to lose the race — the morph would land afterward and wipe
 * out the row this module just injected. The fill is now deferred past the
 * morph (double rAF) and re-checked on every subsequent drawer-open in case
 * the morph still won.
 */
class VellatiniCartRecall {
  /** @type {AbortController | null} */
  #inFlight = null;

  constructor() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    document.addEventListener('theme-drawer:open', this.#onDrawerOpen);
  }

  /**
   * @param {Event} event
   */
  #onDrawerOpen = (event) => {
    // Scope to the cart drawer only — other <theme-drawer> instances (e.g.
    // a chat drawer) share the same event name.
    if (!(event.target instanceof Element) || event.target.id !== 'cart-drawer') return;
    this.#tryFillEmptySlots();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    event.promise
      ?.then(() => {
        // Defer past the morph that follows this promise's resolution: one
        // frame for the morph's DOM writes to land, a second as a margin
        // across browsers/schedulers.
        requestAnimationFrame(() => requestAnimationFrame(this.#tryFillEmptySlots));
      })
      .catch(() => {
        // AbortError or a failed mutation — nothing to fill either way.
      });
  };

  #tryFillEmptySlots = () => {
    for (const slot of document.querySelectorAll('[data-vt-recall-slot]')) {
      if (slot instanceof HTMLElement) this.#fillSlot(slot);
    }
  };

  /**
   * @param {HTMLElement} slot
   */
  #fillSlot(slot) {
    const content = slot.querySelector('[data-vt-recall-content]');

    // Idempotent: skip only if already flagged filled AND the flagged
    // content is still actually there — a morph (or the empty-cart-template
    // swap) can wipe the injected markup without clearing the flag, in which
    // case this must re-fill rather than silently leave the fallback stale.
    if (slot.dataset.vtRecallFilled === 'true' && content?.childElementCount) return;

    const products = RecentlyViewed.getProducts();
    if (!Array.isArray(products) || products.length === 0) return;

    const productId = products[0];
    if (!productId) return;

    this.#inFlight?.abort();
    const controller = new AbortController();
    this.#inFlight = controller;

    const url = new URL(Theme.routes.predictive_search_url, window.location.origin);
    url.searchParams.set('q', `id:${productId}`);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '1');

    sectionRenderer
      .getSectionHTML('vellatini-cart-recall', false, url, controller.signal)
      .then((html) => {
        const trimmed = html.trim();
        if (!trimmed) return; // stale/unavailable product — fallback stands, per rule.

        // Re-query rather than reuse `content` — the slot may have been
        // replaced (empty-cart-template swap) between the fetch starting
        // and resolving.
        const freshContent = slot.querySelector('[data-vt-recall-content]');
        if (!freshContent) return;

        freshContent.innerHTML = trimmed;
        slot.dataset.vtRecallFilled = 'true';

        const eyebrow = slot.querySelector('[data-vt-recall-eyebrow]');
        if (eyebrow) eyebrow.textContent = 'Last viewed';
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[vellatini-cart-recall] fetch failed:', error);
      });
  }
}

new VellatiniCartRecall();
